import { configureStore, isRejectedWithValue, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { showToast } from '../features/toasts/toastsSlice';
import { ToastType } from '../models/toast';
import { DEFAULT_DISMISS_DURATION } from '../providers/ToastProvider';
import { assistantAPI } from '../services/assistant';
import { audiencerateApi } from '../services/audiencerate';
import { rootReducer, RootState } from './index';
import { getErrorMessageForDataSourceMutation } from '@/utils/errors';
import { translateI18nValues } from '@/utils/translationUtil';
import i18n from '../localization';
import { isWhatsappNumberNotConfiguredError } from '@/features/campaigns/v2/components/WhatsappBusinessOnboardingBlock';

const persistConfig = {
  timeout: 0,
  key: 'audiencerate.cdp.root',
  storage,
  blacklist: ['auth', 'sidebar', 'slideOver', 'fileUploading', 'toasts', audiencerateApi.reducerPath, assistantAPI.reducerPath],
};

const errorToastMap: Record<string, number> = {};

// RTK Query reports non-HTTP failures as string statuses; HTTP failures as numbers.
const getErrorTitle = (status: unknown) => {
  switch (status) {
    case 'FETCH_ERROR':
      return i18n.t('server_errors:networkError');
    case 'TIMEOUT_ERROR':
      return i18n.t('server_errors:timeoutError');
    case 'PARSING_ERROR':
    case 'CUSTOM_ERROR':
      return i18n.t('server_errors:unexpectedError');
    default:
      return typeof status === 'number' ? `${i18n.t('ALL:error')} - ${status}` : i18n.t('ALL:error');
  }
};

const getErrorFallbackMessage = (status: unknown) => (typeof status === 'number' ? undefined : i18n.t('server_errors:genericErrorMessage'));

const nonEmptyString = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value : undefined);

type MutationErrorToast = {
  title: string;
  message?: string;
};

/**
 * Toasts use the translated `message` key. The on-screen error keeps `content`.
 * Data-source mutations still send `content` as an array of error codes.
 */
const resolveMutationErrorToast = (payload: { status?: unknown; data?: { message?: unknown; content?: unknown } } | undefined): MutationErrorToast => {
  const content = payload?.data?.content;
  const readableMessage = nonEmptyString(payload?.data?.message);

  if (Array.isArray(content) && content.length > 0) {
    const errorMessage = getErrorMessageForDataSourceMutation({
      responseMessage: typeof payload?.data?.message === 'string' ? payload.data.message : '',
      codes: content,
    });
    return {
      title: getErrorTitle(payload?.status),
      message: errorMessage || getErrorFallbackMessage(payload?.status),
    };
  }

  if (readableMessage) {
    return { title: translateI18nValues(readableMessage, i18n.language) };
  }

  return {
    title: getErrorTitle(payload?.status),
    message: getErrorFallbackMessage(payload?.status),
  };
};

const shouldShowErrorToast = (title: string, message?: string) => {
  const key = `${title}|${message}`;
  const currentTime = errorToastMap[key] || 0;
  const now = Date.now();

  if (currentTime + DEFAULT_DISMISS_DURATION > now) {
    return false;
  } else {
    errorToastMap[key] = now;
    return true;
  }
};

const IGNORED_ENDPOINTS = ['prepareConnectorFlowStep', 'inviteWorkspace', 'deleteDataSource', 'getEmailTemplates'];
export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these use matchers!
  if (isRejectedWithValue(action)) {
    const endpointName = action.meta?.arg?.endpointName;
    if (IGNORED_ENDPOINTS.includes(endpointName)) {
      return next(action);
    }
    // Not a failure: the workspace is connected to Whatsapp Business and has yet to attach a phone
    // number. The screens that hit it offer the activation instead, so a red toast on top of that
    // would only be noise.
    if (isWhatsappNumberNotConfiguredError(action.payload)) {
      return next(action);
    }
    console.log('action', action);
    const { type } = action;
    if (type === 'audiencerateApi/executeMutation/rejected') {
      const errorToast = resolveMutationErrorToast(action.payload);
      if (shouldShowErrorToast(errorToast.title, errorToast.message)) {
        api.dispatch(
          showToast({
            type: ToastType.ERROR,
            title: errorToast.title,
            message: errorToast.message,
          })
        );
      }
    } else if (shouldShowErrorToast(getErrorTitle(action.payload?.status), action.payload?.data?.message)) {
      api.dispatch(
        showToast({
          type: ToastType.ERROR,
          title: getErrorTitle(action.payload?.status),
          message: action.payload?.data?.message || getErrorFallbackMessage(action.payload?.status),
        })
      );
    }
  }

  return next(action);
};

export function configureAppStore(preloadedState: any) {
  const reducer = persistReducer<RootState>(persistConfig, rootReducer);

  const store = configureStore({
    reducer,
    // put here RTK api with .concat on gdm
    middleware: (gdm) =>
      gdm({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
        .concat(rtkQueryErrorLogger)
        .concat(audiencerateApi.middleware)
        .concat(assistantAPI.middleware),
    preloadedState,
  });
  const persistor = persistStore(store);

  setupListeners(store.dispatch);

  return { store, persistor };
}
